import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import Button from '@material-ui/core/Button'
import {
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core'
import {
  getUserInformation,
  getUserChargerInformation,
  getUserEnergySystemInformation,
  getUserMeterInformation,
  getUserThermostatInformation,
  getUserVehicleInformation,
  getProgramEnrollments,
  getDeviceLoadGroups,
} from '../../../actions/admin'
import UpdatePlan from './UpdatePlan'
import VehicleTimeline from './VehicleTimeline'
import VehicleCommands from './VehicleCommands'
import ChargeForecastAdmin from './ChargeForecastAdmin'
import AuthenticationToken from './AuthenticationToken'
import UtilityEligibility from './UtilityEligibility'
import ProgramOverview from './ProgramOverview'
import Rewards from './Rewards'
import ChargeDebugging from './ChargeDebugging'
import EnrollmentHistory from './EnrollmentHistory'
import BillExtraction from './BillExtraction'
import { Alert } from '@material-ui/lab'
import Loader from '../../components/layout/Loader'
import DeviceLoadGroups from '@/authenticated/components/Program/DeviceLoadGroups'

const useStyles = makeStyles((theme) => ({
  root: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
}))

const adminComponents = [
  {
    name: 'Vehicle Timeline',
    component: VehicleTimeline,
  },
  {
    name: 'Vehicle Commands',
    component: VehicleCommands,
  },
  {
    name: 'Update Plan',
    component: UpdatePlan,
  },
  {
    name: 'Charge Forecast',
    component: ChargeForecastAdmin,
  },
  {
    name: 'Authentication Token',
    component: AuthenticationToken,
  },
  {
    name: 'Utility Program Eligibility',
    component: UtilityEligibility,
  },
  {
    name: 'Program Overview',
    component: ProgramOverview,
  },
  {
    name: 'Rewards',
    component: Rewards,
  },
  {
    name: 'Charge Debugging',
    component: ChargeDebugging,
  },
  {
    name: 'Enrollment History',
    component: EnrollmentHistory,
  },
  {
    name: 'Bill Extraction',
    component: BillExtraction,
  },
]

export default function Admin() {
  const dispatch = useDispatch()
  const classes = useStyles()
  const { profileID: adminProfileID } = useParams()
  const [email, setEmail] = useState('')
  const [profileID, setProfileID] = useState(adminProfileID ?? '')
  const [adminComponentIndex, setAdminComponentIndex] = useState(null)
  const [validUser, setValidUser] = useState(true)
  const currentUser = useSelector((state) => state.user.user)
  const user = useSelector((state) => state.admin.user)
  const vehicles = useSelector((state) => state.admin.vehicles)
  const thermostats = useSelector((state) => state.admin.thermostats)
  const chargers = useSelector((state) => state.admin.chargers)
  const meters = useSelector((state) => state.admin.meters)
  const energySystems = useSelector((state) => state.admin.energySystems)
  const enrollments = useSelector((state) => state.admin.programEnrollments)
  const deviceLoadGroups = useSelector((state) => state.admin.deviceLoadGroups)

  useEffect(() => {
    setValidUser(!!user?.profile?.id)
    if (user?.profile?.id) {
      setEmail(user?.username)
      setProfileID(user?.profile?.id)
      const encoded_email = encodeURIComponent(email)
      dispatch(getUserVehicleInformation(encoded_email, user?.profile?.id))
      dispatch(getUserChargerInformation(encoded_email, user?.profile?.id))
      dispatch(getUserThermostatInformation(encoded_email, user?.profile?.id))
      dispatch(getUserEnergySystemInformation(encoded_email, user?.profile?.id))
      dispatch(getUserMeterInformation(encoded_email, user?.profile?.id))
      dispatch(getProgramEnrollments(user?.profile?.id))
      dispatch(getDeviceLoadGroups(user?.profile?.id))
    } else {
      setEmail('')
      setProfileID('')
    }
  }, [user])

  useEffect(() => {
    if (adminProfileID) {
      getUserDetails()
    }
  }, [adminProfileID])

  function changeProfile(e) {
    setProfileID(e.target.value)
    setEmail('')
  }

  function changeEmail(e) {
    setEmail(e.target.value)
    setProfileID('')
  }

  function changeAdminComponent(componentIndex) {
    if (componentIndex === adminComponentIndex) {
      setAdminComponentIndex(null)
    } else {
      setAdminComponentIndex(componentIndex)
    }
  }

  function getUserDetails() {
    dispatch(getUserInformation(email, profileID))
  }
  function get_model_content_type(content_type) {
    switch (content_type) {
      case 139:
        return 'Tesla'
      case 143:
        return 'Smartcar'
      case 270:
        return 'Ford'
      default:
        return 'Unknown'
    }
  }

  const profile = user?.profile
  const AdminComponent = adminComponents?.[adminComponentIndex]?.component

  if (!currentUser) {
    return <Loader />
  }

  return (
    <div className={classes.root}>
      <Grid container>
        <Grid item xs={12}>
          <Alert severity="info">
            You are currently signed in as:{' '}
            <strong>{currentUser?.username}</strong>
          </Alert>
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField label="Email" value={email} onChange={changeEmail} />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Divider orientation="vertical" />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            label="Profile ID"
            value={profileID}
            onChange={changeProfile}
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <Button color="primary" variant="contained" onClick={getUserDetails}>
            Get User Information
          </Button>
        </Grid>
        {!validUser && (
          <Grid item xs={12}>
            <Alert severity="Warning">
              Please enter a valid email or profile ID
            </Alert>
          </Grid>
        )}
      </Grid>
      {/* Only show the rest of the UI if we have a valid user */}
      {user && profileID && (
        <div>
          <h6>User Information</h6>
          <Grid container mt={4}>
            <Grid item>
              <TableContainer component={Paper}>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>User ID</TableCell>
                      <TableCell>Profile ID</TableCell>
                      <TableCell>
                        External Profile Id (utility_ref_id)
                      </TableCell>
                      <TableCell>First Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Home Address</TableCell>
                      <TableCell>Utility Name</TableCell>
                      <TableCell>Plan Name</TableCell>
                      <TableCell>Plan Type</TableCell>
                      <TableCell>Bill Url</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{user?.id}</TableCell>
                      <TableCell>{user?.profile?.id}</TableCell>
                      <TableCell>{profile?.utility_ref_id}</TableCell>
                      <TableCell>{user?.first_name}</TableCell>
                      <TableCell>{user?.username}</TableCell>
                      <TableCell>{profile?.home?.formatted_address}</TableCell>
                      <TableCell>{profile?.utility?.name}</TableCell>
                      <TableCell>{profile?.plan?.name}</TableCell>
                      <TableCell>{profile?.plan?.types}</TableCell>
                      <TableCell
                        component="a"
                        href={profile?.utility_bill_upload?.s3_url}
                        target="_blank"
                      >
                        {profile?.utility_bill_upload ? 'Bill' : null}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {/* Vehicles info */}
            </Grid>
            {!!vehicles && !!vehicles.length && (
              <Grid container mt={4}>
                <Grid item>
                  <h6>Vehicles Info</h6>
                  <TableContainer component={Paper}>
                    <Table className={classes.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell>id</TableCell>
                          <TableCell>created</TableCell>
                          <TableCell>Inactive</TableCell>
                          <TableCell>Authentication Inactive</TableCell>
                          <TableCell>User Deleted</TableCell>
                          <TableCell>Is Selected Vehicle</TableCell>
                          <TableCell>Controllable</TableCell>
                          <TableCell>Model Content Type</TableCell>
                          <TableCell>Car id</TableCell>
                          <TableCell>Car Name</TableCell>
                          <TableCell>Car Permissions</TableCell>
                          <TableCell>Car Model Name</TableCell>
                          <TableCell>Year</TableCell>
                          <TableCell>Trim</TableCell>
                          <TableCell>Drivetrain</TableCell>
                          <TableCell>Battery (kWh)</TableCell>
                          <TableCell>VIN</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {vehicles.map((vehicle) => (
                          <TableRow key={vehicle.id}>
                            <TableCell>{vehicle.id}</TableCell>
                            <TableCell>
                              {vehicle.created
                                ? new Date(vehicle.created).toLocaleString()
                                : '—'}
                            </TableCell>
                            <TableCell>{String(vehicle.inactive)}</TableCell>
                            <TableCell>
                              {String(vehicle.authentication_inactive)}
                            </TableCell>
                            <TableCell>
                              {String(vehicle.user_deleted)}
                            </TableCell>
                            <TableCell>
                              {String(vehicle.is_selected_vehicle)}
                            </TableCell>
                            <TableCell>
                              {String(vehicle.controllable)}
                            </TableCell>
                            <TableCell>
                              {vehicle.model_content_type} -{' '}
                              {get_model_content_type(
                                vehicle.model_content_type,
                              )}
                            </TableCell>
                            <TableCell>{vehicle.car.id}</TableCell>
                            <TableCell>{vehicle?.car?.display_name}</TableCell>
                            <TableCell>
                              {vehicle?.car?.permissions.join(', ')}
                            </TableCell>
                            <TableCell>
                              {vehicle?.car?.car_model?.friendly_name}
                            </TableCell>
                            <TableCell>
                              {vehicle?.car?.car_model?.year ?? '—'}
                            </TableCell>
                            <TableCell>
                              {vehicle?.car?.car_model?.trim ?? '—'}
                            </TableCell>
                            <TableCell>
                              {vehicle?.car?.car_model?.drivetrain ?? '—'}
                            </TableCell>
                            <TableCell>
                              {vehicle?.car?.car_model?.battery_capacity_kwh ??
                                '—'}
                            </TableCell>
                            <TableCell>{vehicle?.car?.vin ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
            {/* Charger info */}
            {!!chargers && !!chargers.length && (
              <Grid container mt={4}>
                <Grid item>
                  <h6>Charger Info</h6>
                  <TableContainer component={Paper}>
                    <Table className={classes.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell>id</TableCell>
                          <TableCell>created</TableCell>
                          <TableCell>name</TableCell>
                          <TableCell>Authentication Inactive</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {chargers.map((charger) => (
                          <TableRow key={charger.id}>
                            <TableCell>{charger.id}</TableCell>
                            <TableCell>
                              {charger.created
                                ? new Date(charger.created).toLocaleString()
                                : '—'}
                            </TableCell>
                            <TableCell>{charger.name}</TableCell>
                            <TableCell>
                              {String(charger.authentication_inactive)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
            {/* Thermostat info */}
            {!!thermostats && !!thermostats.length && (
              <Grid container mt={4}>
                <Grid item>
                  <h6>Thermostat Info</h6>
                  <TableContainer component={Paper}>
                    <Table className={classes.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell>id</TableCell>
                          <TableCell>created</TableCell>
                          <TableCell>portal</TableCell>
                          <TableCell>name</TableCell>
                          <TableCell>Authentication Inactive</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {thermostats.map((thermostat) => (
                          <TableRow key={thermostat.id}>
                            <TableCell>{thermostat.id}</TableCell>
                            <TableCell>
                              {thermostat.created
                                ? new Date(thermostat.created).toLocaleString()
                                : '—'}
                            </TableCell>
                            <TableCell>{thermostat.portal}</TableCell>
                            <TableCell>{thermostat.name}</TableCell>
                            <TableCell>
                              {String(thermostat.authentication_inactive)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
            {/* Energy System info */}
            {!!energySystems && !!energySystems.length && (
              <Grid container mt={4}>
                <Grid item>
                  <h6>Energy System Info</h6>
                  <TableContainer component={Paper}>
                    <Table className={classes.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell>id</TableCell>
                          <TableCell>status</TableCell>
                          <TableCell>name</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {energySystems.map((energySystem) => (
                          <TableRow key={energySystem.id}>
                            <TableCell>{energySystem.id}</TableCell>
                            <TableCell>{energySystem.status}</TableCell>
                            <TableCell>{energySystem.name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
            {/* Meter info */}
            {!!meters && !!meters.length && (
              <Grid container mt={4}>
                <Grid item>
                  <h6>Meter Info</h6>
                  <TableContainer component={Paper}>
                    <Table className={classes.table}>
                      <TableHead>
                        <TableRow>
                          <TableCell>id</TableCell>
                          <TableCell>Leap meter id</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Transmission region</TableCell>
                          <TableCell>Enrollment JSON</TableCell>
                          <TableCell>Data JSON</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {meters.map((meter) => (
                          <TableRow key={meter.id}>
                            <TableCell>{meter.id}</TableCell>
                            <TableCell>
                              <a
                                href={`https://partner.leap.energy/meters?selected=${meter.leap_meter_id}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {meter.leap_meter_id}
                              </a>
                            </TableCell>
                            <TableCell>{meter.status}</TableCell>
                            <TableCell>{meter.transmission_region}</TableCell>
                            <TableCell>
                              <div
                                style={{
                                  maxHeight: 100,
                                  maxWidth: 400,
                                  overflow: 'auto',
                                  background: '#f9f9f9',
                                  padding: '8px',
                                  borderRadius: '4px',
                                }}
                              >
                                <pre style={{ margin: 0, fontSize: '0.8rem' }}>
                                  {JSON.stringify(
                                    meter.enrollment_json,
                                    null,
                                    2,
                                  )}
                                </pre>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div
                                style={{
                                  maxHeight: 100,
                                  maxWidth: 400,
                                  overflow: 'auto',
                                  background: '#f9f9f9',
                                  padding: '8px',
                                  borderRadius: '4px',
                                }}
                              >
                                <pre style={{ margin: 0, fontSize: '0.8rem' }}>
                                  {JSON.stringify(meter.data_json, null, 2)}
                                </pre>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
            {!!enrollments && enrollments.length > 0 && (
              <Grid container mt={4}>
                <Grid item xs={12}>
                  <h6>Utility Program Enrollments</h6>
                </Grid>

                {enrollments.map((program_enrollment) => {
                  const {
                    id,
                    utility_program,
                    vehicle_enrollments,
                    thermostat_enrollments,
                    charger_enrollments,
                    behavioral_enrollments,
                    energy_system_enrollments,
                  } = program_enrollment

                  const rows = []

                  const add_row = (type, enrollment) => {
                    // Get device name/info for better display
                    let device_display = '—'
                    let device_id = '—'

                    if (enrollment?.vehicle_id) {
                      const vehicle = vehicles?.find(
                        (v) => v.id === enrollment.vehicle_id,
                      )
                      device_id = enrollment.vehicle_id
                      device_display =
                        vehicle?.car?.display_name ||
                        vehicle?.car?.car_model?.friendly_name ||
                        `Vehicle #${enrollment.vehicle_id}`
                    } else if (enrollment?.thermostat_id) {
                      const thermostat = thermostats?.find(
                        (t) => t.id === enrollment.thermostat_id,
                      )
                      device_id = enrollment.thermostat_id
                      device_display =
                        thermostat?.name ||
                        `Thermostat #${enrollment.thermostat_id}`
                    } else if (enrollment?.charger_id) {
                      const charger = chargers?.find(
                        (c) => c.id === enrollment.charger_id,
                      )
                      device_id = enrollment.charger_id
                      device_display =
                        charger?.name || `Charger #${enrollment.charger_id}`
                    } else if (enrollment?.energy_system_id) {
                      const energySystem = energySystems?.find(
                        (es) => es.id === enrollment.energy_system_id,
                      )
                      device_id = enrollment.energy_system_id
                      device_display =
                        energySystem?.name ||
                        `Energy System #${enrollment.energy_system_id}`
                    }

                    rows.push({
                      type,
                      created: enrollment.created
                        ? new Date(enrollment.created).toLocaleString()
                        : '—',
                      device_id,
                      device_display,
                      id: enrollment.id || '—',
                      external_id: enrollment.external_id || '—',
                      enrolled: enrollment.enrolled ? 'Yes' : 'No',
                      eligibility_status: enrollment.eligibility_status || '—',
                      participation_status:
                        enrollment.participation_status || '—',
                      group: enrollment?.group?.alias || '—',
                      unenrolled_at: enrollment.unenrolled_at
                        ? new Date(enrollment.unenrolled_at).toLocaleString()
                        : '—',
                      reason:
                        enrollment?.unenrolled_reasons?.auto_unenroll_reasons ||
                        '—',
                    })
                  }

                  // Add rows for each enrollment type if they exist
                  vehicle_enrollments.forEach((ve) => add_row('Vehicle', ve))
                  thermostat_enrollments.forEach((te) =>
                    add_row('Thermostat', te),
                  )
                  charger_enrollments.forEach((ce) => add_row('Charger', ce))
                  behavioral_enrollments.forEach((be) =>
                    add_row('Behavioral', be),
                  )
                  energy_system_enrollments.forEach((ese) =>
                    add_row('Battery', ese),
                  )

                  // Fallback if no enrollments
                  if (rows.length === 0) {
                    rows.push({
                      type: '—',
                      device_display: 'No device enrollments',
                      device_id: '—',
                      id: '—',
                      external_id: '—',
                      created: '—',
                      enrolled: '—',
                      participation_status: 'None',
                      eligibility_status: 'None',
                      group: '—',
                      unenrolled_at: '—',
                      reason: '—',
                    })
                  }

                  return (
                    <Grid item xs={12} key={id}>
                      <TableContainer component={Paper}>
                        <Table className={classes.table}>
                          <TableHead>
                            <TableRow>
                              <TableCell colSpan={6}>
                                <strong>{utility_program.name}</strong>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Type</TableCell>
                              <TableCell>Created Time (Local Time)</TableCell>
                              <TableCell>Enrollment ID</TableCell>
                              <TableCell>External Enrollment ID</TableCell>
                              <TableCell>Verification Status</TableCell>
                              <TableCell>Verified At</TableCell>
                              <TableCell>API Metadata</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                Utility Program Enrollment Overview
                              </TableCell>
                              <TableCell>
                                {program_enrollment.created
                                  ? new Date(
                                      program_enrollment.created,
                                    ).toLocaleString()
                                  : '—'}
                              </TableCell>
                              <TableCell>{id}</TableCell>
                              <TableCell>
                                {program_enrollment.external_id || '—'}
                              </TableCell>
                              <TableCell>
                                {program_enrollment.verification_status || '—'}
                              </TableCell>
                              <TableCell>
                                {program_enrollment.verified_at
                                  ? new Date(
                                      program_enrollment.verified_at,
                                    ).toLocaleString()
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                <div
                                  style={{
                                    maxHeight: 100,
                                    maxWidth: 400,
                                    overflow: 'auto',
                                    background: '#f9f9f9',
                                    padding: '8px',
                                    borderRadius: '4px',
                                  }}
                                >
                                  <pre
                                    style={{ margin: 0, fontSize: '0.8rem' }}
                                  >
                                    {JSON.stringify(
                                      program_enrollment.api_metadata,
                                      null,
                                      2,
                                    )}
                                  </pre>
                                </div>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Type</TableCell>
                              <TableCell>Device</TableCell>
                              <TableCell>Device ID</TableCell>
                              <TableCell>Created Time (Local Time)</TableCell>
                              <TableCell>Enrollment ID</TableCell>
                              <TableCell>External Enrollment ID</TableCell>
                              <TableCell>Enrolled</TableCell>
                              <TableCell>Eligibility Status</TableCell>
                              <TableCell>Participation Status</TableCell>
                              <TableCell>Group</TableCell>
                              <TableCell>Unenrolled At</TableCell>
                              <TableCell>Unenroll Reason</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.map((row, index) => (
                              <TableRow key={index}>
                                <TableCell>{row.type}</TableCell>
                                <TableCell>{row.device_display}</TableCell>
                                <TableCell>{row.device_id}</TableCell>
                                <TableCell>{row.created}</TableCell>
                                <TableCell>{row.id}</TableCell>
                                <TableCell>{row.external_id}</TableCell>
                                <TableCell>{row.enrolled}</TableCell>
                                <TableCell>{row.eligibility_status}</TableCell>
                                <TableCell>
                                  {row.participation_status}
                                </TableCell>
                                <TableCell>{row.group}</TableCell>
                                <TableCell>{row.unenrolled_at}</TableCell>
                                <TableCell>{row.reason}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  )
                })}
              </Grid>
            )}
            {!!deviceLoadGroups &&
              deviceLoadGroups.ProgramVehicleEnrollment && (
                <DeviceLoadGroups
                  enrollmentData={deviceLoadGroups.ProgramVehicleEnrollment}
                />
              )}
          </Grid>
          <Grid container>
            <Grid item>
              {adminComponents.map((elem, index) => (
                <Button
                  key={index}
                  variant="contained"
                  color="primary"
                  onClick={() => changeAdminComponent(index)}
                >
                  {elem.name}
                </Button>
              ))}
            </Grid>
          </Grid>
          <Grid container>
            {adminComponentIndex !== null ? (
              <AdminComponent userEmail={email} profileID={profileID} />
            ) : null}
          </Grid>
        </div>
      )}
    </div>
  )
}
